import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import config from "../../../config/config";
import { faEdit, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import {
  FaClipboardList,
  FaCalendarAlt,
  FaDollarSign,
  FaFileAlt,
  FaBusinessTime,
} from "react-icons/fa";
import { HiOutlineCheckCircle } from "react-icons/hi2";
import { HiUserGroup } from "react-icons/hi";
const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const formatToLocalTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(date);
};

const AgreementDetails = ({ agreement, user }) => {
  const navigate = useNavigate();

  if (!["worker", "client"].includes(user.userType.toLowerCase())) {
    console.error("Invalid userType:", user.userType);
    return <div>Access Denied: Invalid user type.</div>;
  }

  if (!agreement) {
    return (
      <div>
        No agreement details available. Please go back and fill out the form.
      </div>
    );
  }

  const {
    jobId,
    jobTitle,
    jobDescription,
    clientName,
    jobLocation,
    totalAgreementHours,
    rates,
    noFeeForInitialGreetingSession,
    initialGreetingSessionRate,
    startDate,
    endDate,
    frequency,
    editorContent,
    dailyAvailability,
    travelRatePerMile,
    agreementDate,
  } = agreement;

  const renderEndDate = () => {
    if (endDate && endDate.day && endDate.month && endDate.year) {
      return (
        <div className="agreement-details-review-section">
          <h3>
            <FaCalendarAlt className="icon-common icon-calendar" />
            Expected End Date
          </h3>
          <div className="agreement-section-item">{`${endDate.day}/${endDate.month}/${endDate.year}`}</div>
        </div>
      );
    }
    return (
      <div className="agreement-details-review-section">
        <h3>
          <FaCalendarAlt className="icon-common icon-calendar" />
          Expected End Date
        </h3>
        <div className="agreement-section-item">N/A</div>
      </div>
    );
  };

  const renderDaysWithTimeSlots = () => {
    return daysOfWeek.map((day, index) => {
      const availability = dailyAvailability[day] || [];
      if (availability.length > 0) {
        return (
          <div key={index}>
            <p>
              <strong>{day}</strong>
              <HiOutlineCheckCircle
                style={{
                  marginLeft: "5px",
                  color: "green",
                  verticalAlign: "middle",
                }}
              />
            </p>
            <ul>
              {availability.map((slot, i) => (
                <li key={i}>
                  {slot.from} - {slot.to}
                </li>
              ))}
            </ul>
          </div>
        );
      }
      return null;
    });
  };

  const renderAdditionalConsiderations = () => {
    if (editorContent && editorContent.trim() !== "") {
      return (
        <div className="agreement-details-review-section">
          <h3>
            <FaFileAlt className="icon-common icon-file" />
            Additional Considerations
          </h3>
          <div
            className="agreement-section-item"
            dangerouslySetInnerHTML={{ __html: editorContent }}
          ></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="agreement-details-review-container">
      <div className="agreement-review-header">
        <h2>Agreement Details</h2>
        <p>
          Agreement ID:{" "}
          <span>
            <strong>{agreement.agreementId}</strong>
          </span>
        </p>
      </div>
      <div className="agreement-details-review-section">
        <h3>
          <HiUserGroup className="icon-common icon-user" />
          Parties Information
        </h3>
        <div className="agreement-section-item job-title-styled">
          <p>
            This agreement is entered into by{" "}
            <span>
              {user.userType === "worker"
                ? `${user.firstName} ${user.lastName}`
                : agreement.workerName}
            </span>{" "}
            hereinafter referred to as "<strong>Worker</strong>," and{" "}
            <span>{clientName}</span> hereinafter referred to as "
            <strong>Client</strong>."
          </p>
        </div>
      </div>
      <div className="agreement-details-review-section">
        <h3>
          <FaClipboardList className="icon-common icon-list" />
          Job details
        </h3>
        <p>
          <strong>Job Title</strong>
        </p>
        <div className="agreement-section-item">{jobTitle}</div>
        <div>
          <h4>Job Description</h4>
          <div className="agreement-section-item job-title-styled">
            {jobDescription}
          </div>
          <p></p>
        </div>
        <p>
          <strong>Job Frequency:</strong>
        </p>
        <div className="agreement-section-item">{frequency}</div>
        <p>
          <strong>Job Location:</strong>
        </p>
        <div className="agreement-section-item">{jobLocation}</div>
      </div>
      <div className="agreement-details-review-section">
        <h3>
          <FaCalendarAlt className="icon-common icon-calendar" />
          Expected Start Date
        </h3>
        <div className="agreement-section-item">{`${startDate.day}/${startDate.month}/${startDate.year}`}</div>
      </div>
      {renderEndDate()}
      <div className="agreement-details-review-section">
        <h3>
          <FaCalendarAlt className="icon-common icon-calendar" />
          Agreement Days and Time Slots
        </h3>
        {renderDaysWithTimeSlots()}
      </div>
      <div className="agreement-details-review-section">
        <h3>
          <FaBusinessTime className="icon-common icon-dollar" />
          Total Agreement Hours
        </h3>
        <div className="agreement-section-item">{totalAgreementHours}</div>
      </div>
      <div className="agreement-details-review-section">
        <h3>
          <FaDollarSign className="icon-common icon-dollar" />
          Agreed Rates
        </h3>
        {Object.entries(rates).map(([day, rate], index) => {
          if (agreement.checkedDays[day]) {
            const originalRate = parseFloat(rate);
            const reducedRate = (originalRate * 0.93).toFixed(2);
            return (
              <div key={index} className="rate-agreement-review-content">
                <p>
                  <strong>{`${day} `}</strong>
                </p>
                <div className="agreement-section-item">${rate}</div>
                <div className="net-rate-note">
                  Net rate you will be paid after <strong>7%</strong> OSH
                  Platform fee is <strong>${reducedRate}</strong>
                </div>
                {day === "Full Day" && (
                  <div className="net-rate-note">
                    Note: Full Day (24-Hour) Flat rate covers all support
                    (active, passive, & sleep)
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </div>
      {travelRatePerMile && (
        <div className="agreement-details-review-section">
          <h3>
            <FaDollarSign className="icon-common icon-dollar" />
            Travel Rate Per Mile
          </h3>
          <div className="rate-agreement-review-content">
            <div className="agreement-section-item">${travelRatePerMile}</div>
          </div>
        </div>
      )}
      <div className="agreement-details-review-section">
        <h3>
          <FaDollarSign className="icon-common icon-dollar" />
          Meet and Greet Session Rate
        </h3>
        <div className="rate-agreement-review-content">
          <div className="agreement-section-item">
            {noFeeForInitialGreetingSession
              ? "Free"
              : `$${initialGreetingSessionRate}`}
          </div>
        </div>
      </div>
      {renderAdditionalConsiderations()}
      <div className="agreement-details-review-section">
        <h3>
          <FaCalendarAlt className="icon-common icon-calendar" />
          Agreement Submission Date
        </h3>
        <div className="rate-agreement-review-content">
          <div className="agreement-section-item">
            {agreementDate ? formatToLocalTime(agreementDate) : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementDetails;
